import AppContext from 'App/AppContext';
import { copilotPanelResultBus } from 'models/copilot/copilotPanelResultBus';
import { EntityTypes } from 'models/entity';
import { useContext, useEffect, useRef } from 'react';

/**
 * Reloads a detail scene's entity after a Copilot answer or an approval about it: the agent may have changed
 * it server-side. Filters on the entity, not on the panel context, so an approval under another usage (the
 * control audit) counts too. Unsaved edits win: no reload while the scene is dirty, and an edit started while
 * the GET was in flight drops the result.
 */
export const useCopilotEntityReload = <T>(
  entityType: EntityTypes,
  entityId: number | undefined,
  hasUnsavedChanges: boolean,
  fetch: (entityId: number) => Promise<T | undefined>,
  apply: (entity: T) => void,
): void => {
  const appContext = useContext(AppContext);
  const hasUnsavedChangesRef = useRef<boolean>(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useEffect(() => {
    if (!entityId) return;

    const subscription = copilotPanelResultBus.subscribeGeneralCompletion((_context, type, id) => {
      if (type !== entityType || id !== entityId) return;
      if (hasUnsavedChangesRef.current) return;

      fetchRef
        .current(entityId)
        .then((entity) => {
          if (entity === undefined || hasUnsavedChangesRef.current) return;

          applyRef.current(entity);
        })
        .catch((err) => appContext.setError(err));
    });

    return () => subscription.dispose();
  }, [appContext, entityId, entityType]);
};
